import React, { useEffect, useMemo, useState } from 'react';
import { Badge } from './ui/badge';
import { Users, UserCheck, X, Tag as TagIcon, ChevronDown, ChevronUp } from 'lucide-react';
import SearchableAthleteSelect from './SearchableAthleteSelect';
import { AthleteAvatar } from './AthleteAvatar';

/**
 * Reusable Target Audience Selector for all modules.
 *
 * Allows staff/admin to target content to:
 *   - All Athletes (overrides everything)
 *   - Specific squads (multi-select, dynamically loaded)
 *   - Specific tags (dynamic) — auto-resolves to assigned athletes
 *   - Specific athletes (multi-select with search)
 *   - Combination of any/all of the above
 *
 * Props:
 *   value:    { target_type, target_squads, target_athletes, target_tag_ids }
 *   onChange: (value) => void
 *   athletes: array of athlete objects [{id, full_name, squad, status, ...}]
 *   tags:     array of tag objects [{id, name, color}] — optional
 *   tagMap:   optional { athlete_id: [tag_id,...] } — if omitted the component
 *             lazy-fetches /api/tags/map once on mount (live-fetch fallback).
 */
const TargetAudienceSelector = ({
  athletes = [],
  tags = [],
  tagMap: tagMapProp = null,
  value = { target_type: 'all', target_squads: [], target_athletes: [], target_tag_ids: [] },
  onChange,
  label = 'Target Audience',
  compact = false,
  'data-testid': testId = 'target-audience',
}) => {
  const [showAthleteSelect, setShowAthleteSelect] = useState(
    value.target_type === 'custom' && (value.target_athletes?.length > 0)
  );
  // Resolved athletes are shown expanded by default so selecting a tag always
  // reveals the assigned athletes (previously hidden behind a collapsed toggle).
  const [showResolvedDetail, setShowResolvedDetail] = useState(true);
  const [fetchedTagMap, setFetchedTagMap] = useState(null);
  // Authoritative per-tag athlete ids ({ tag_id: [athlete_id,...] }) fetched from
  // /api/tags/{id}/athletes — a reliable fallback independent of the bulk map,
  // so tag resolution still works if the bulk /tags/map fetch fails or is stale.
  const [tagEndpointIds, setTagEndpointIds] = useState({});

  // Live-fetch fallback: only when caller didn't pass tagMap and there are tags to resolve.
  useEffect(() => {
    if (tagMapProp || tags.length === 0) return;
    let cancelled = false;
    const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
    if (!token) return;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tags/map`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => (r.ok ? r.json() : null))
      .then(json => { if (!cancelled && json && typeof json === 'object') setFetchedTagMap(json); })
      .catch(() => { /* silent — endpoint fallback below still resolves athletes */ });
    return () => { cancelled = true; };
  }, [tagMapProp, tags.length]);

  const tagMap = tagMapProp || fetchedTagMap || {};

  // Authoritative fallback: for each newly-selected tag, fetch its assigned athlete
  // ids directly. This guarantees resolution even when the bulk map is unavailable.
  const selectedTagKey = (value.target_tag_ids || []).join(',');
  useEffect(() => {
    const ids = (value.target_tag_ids || []);
    const missing = ids.filter(id => !(id in tagEndpointIds));
    if (missing.length === 0) return;
    const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
    if (!token) return;
    let cancelled = false;
    Promise.all(missing.map(id =>
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tags/${id}/athletes`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then(r => (r.ok ? r.json() : []))
        .then(list => [id, Array.isArray(list) ? list : []])
        .catch(() => [id, []])
    )).then(pairs => {
      if (!cancelled) setTagEndpointIds(prev => ({ ...prev, ...Object.fromEntries(pairs) }));
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTagKey]);

  // Only operate on active athletes by default — inactive never appear in selectable lists.
  const selectableAthletes = useMemo(
    () => athletes.filter(a => (a.status || 'active') === 'active'),
    [athletes]
  );

  const squads = useMemo(() => {
    const s = new Set();
    selectableAthletes.forEach(a => { if (a.squad) s.add(a.squad); });
    return [...s].sort();
  }, [selectableAthletes]);

  const isAll = value.target_type === 'all';
  const selectedSquads = value.target_squads || [];
  const selectedAthletes = value.target_athletes || [];
  const selectedTagIds = value.target_tag_ids || [];

  // Resolve which athletes belong to any of the selected tags — via the bulk
  // tagMap OR the authoritative per-tag endpoint (whichever is available).
  const tagResolvedAthletes = useMemo(() => {
    if (selectedTagIds.length === 0) return [];
    const tagSet = new Set(selectedTagIds);
    const endpointIdSet = new Set(
      selectedTagIds.flatMap(id => tagEndpointIds[id] || [])
    );
    return selectableAthletes.filter(a => {
      const aTags = tagMap[a.id] || [];
      return aTags.some(t => tagSet.has(t)) || endpointIdSet.has(a.id);
    });
  }, [selectedTagIds, tagMap, selectableAthletes, tagEndpointIds]);

  const emit = (next) => {
    const hasCustom =
      (next.target_squads || []).length > 0 ||
      (next.target_athletes || []).length > 0 ||
      (next.target_tag_ids || []).length > 0;
    onChange({
      target_type: hasCustom ? 'custom' : 'all',
      target_squads: next.target_squads || [],
      target_athletes: next.target_athletes || [],
      target_tag_ids: next.target_tag_ids || [],
    });
  };

  const handleAllToggle = () => {
    if (isAll) return;
    onChange({ target_type: 'all', target_squads: [], target_athletes: [], target_tag_ids: [] });
    setShowAthleteSelect(false);
    setShowResolvedDetail(false);
  };

  const handleSquadToggle = (squad) => {
    const newSquads = selectedSquads.includes(squad)
      ? selectedSquads.filter(s => s !== squad)
      : [...selectedSquads, squad];
    emit({ target_squads: newSquads, target_athletes: selectedAthletes, target_tag_ids: selectedTagIds });
  };

  const handleTagToggle = (tagId) => {
    const newTags = selectedTagIds.includes(tagId)
      ? selectedTagIds.filter(t => t !== tagId)
      : [...selectedTagIds, tagId];
    if (newTags.length > 0) setShowResolvedDetail(true);
    emit({ target_squads: selectedSquads, target_athletes: selectedAthletes, target_tag_ids: newTags });
  };

  const handleAthletesChange = (ids) => {
    emit({ target_squads: selectedSquads, target_athletes: ids, target_tag_ids: selectedTagIds });
  };

  const toggleSpecificAthletes = () => {
    if (showAthleteSelect) {
      setShowAthleteSelect(false);
      if (selectedAthletes.length > 0) {
        emit({ target_squads: selectedSquads, target_athletes: [], target_tag_ids: selectedTagIds });
      }
    } else {
      setShowAthleteSelect(true);
      // Phase 73 fix: opening Specific Athletes must immediately flip the
      // selector out of "All Athletes" mode so the picker actually renders.
      // The picker is gated on `!isAll`, and isAll is derived from
      // target_type === 'all'. We flip to 'custom' even though no athlete
      // is selected yet — the visual summary stays clean because all
      // selection arrays remain empty.
      if (isAll) {
        onChange({
          target_type: 'custom',
          target_squads: selectedSquads,
          target_athletes: [],
          target_tag_ids: selectedTagIds,
        });
      }
    }
  };

  const getSummary = () => {
    if (isAll) return null;
    const parts = [];
    if (selectedSquads.length > 0) parts.push(selectedSquads.join(', '));
    if (selectedTagIds.length > 0) {
      const names = selectedTagIds.map(id => tags.find(t => t.id === id)?.name).filter(Boolean);
      if (names.length > 0) {
        const tagLabel = `#${names.join(', #')}`;
        parts.push(
          tagResolvedAthletes.length > 0
            ? `${tagLabel} (${tagResolvedAthletes.length} athlete${tagResolvedAthletes.length > 1 ? 's' : ''} via tags)`
            : tagLabel
        );
      }
    }
    if (selectedAthletes.length > 0) {
      parts.push(`${selectedAthletes.length} athlete${selectedAthletes.length > 1 ? 's' : ''}`);
    }
    return parts.length > 0 ? parts.join(' + ') : null;
  };

  const summary = getSummary();
  const hasResolvedAthletes = !isAll && tagResolvedAthletes.length > 0;

  return (
    <div data-testid={testId}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{label}</label>
      )}

      <div className={`flex flex-wrap gap-1.5 ${compact ? 'mb-2' : 'mb-3'}`}>
        <button
          type="button"
          onClick={handleAllToggle}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
            isAll
              ? 'bg-emerald-600 text-white shadow-sm'
              : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
          }`}
          data-testid={`${testId}-all`}
        >
          <Users className="w-3 h-3" />
          All Athletes
        </button>

        {squads.map(squad => (
          <button
            key={squad}
            type="button"
            onClick={() => handleSquadToggle(squad)}
            className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
              !isAll && selectedSquads.includes(squad)
                ? 'bg-emerald-600 text-white shadow-sm'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
            data-testid={`${testId}-squad-${squad}`}
          >
            {squad}
            {!isAll && selectedSquads.includes(squad) && <X className="w-2.5 h-2.5 ml-0.5" />}
          </button>
        ))}

        {tags.map(t => {
          const active = selectedTagIds.includes(t.id);
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => handleTagToggle(t.id)}
              className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium transition-all border ${
                active ? 'text-white shadow-sm' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
              style={active ? { background: t.color || '#10b981', borderColor: t.color || '#10b981' } : { borderColor: 'transparent' }}
              data-testid={`${testId}-tag-${t.id}`}
              title={t.description || t.name}
            >
              <TagIcon className="w-3 h-3" />
              {t.name}
              {active && <X className="w-2.5 h-2.5 ml-0.5" />}
            </button>
          );
        })}

        <button
          type="button"
          onClick={toggleSpecificAthletes}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
            showAthleteSelect && !isAll
              ? 'bg-blue-600 text-white shadow-sm'
              : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
          }`}
          data-testid={`${testId}-specific`}
        >
          <UserCheck className="w-3 h-3" />
          Specific Athletes
          {selectedAthletes.length > 0 && (
            <Badge className="ml-1 bg-white/20 text-current text-[10px] px-1 py-0 h-4 min-w-[16px] flex items-center justify-center">
              {selectedAthletes.length}
            </Badge>
          )}
        </button>
      </div>

      {summary && (
        <p className="text-[11px] text-gray-500 dark:text-gray-400 mb-2" data-testid={`${testId}-summary`}>
          Targeting: {summary}
        </p>
      )}

      {hasResolvedAthletes && (
        <div className="mb-2" data-testid={`${testId}-resolved-section`}>
          <button
            type="button"
            onClick={() => setShowResolvedDetail(v => !v)}
            className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-700 dark:text-emerald-400 hover:text-emerald-800 dark:hover:text-emerald-300 transition-colors"
            data-testid={`${testId}-resolved-toggle`}
            aria-expanded={showResolvedDetail}
          >
            {showResolvedDetail ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            {showResolvedDetail ? 'Hide' : 'Show'} {tagResolvedAthletes.length} athlete{tagResolvedAthletes.length > 1 ? 's' : ''} via tags
          </button>
          {showResolvedDetail && (
            <div
              className="mt-1.5 p-2 rounded-md bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-900/40"
              data-testid={`${testId}-resolved-list`}
            >
              <div className="flex flex-wrap gap-1.5">
                {tagResolvedAthletes.map(a => (
                  <span
                    key={a.id}
                    className="inline-flex items-center gap-1.5 pl-1 pr-2 py-0.5 rounded-full bg-white dark:bg-gray-800 text-[11px] text-gray-700 dark:text-gray-200 border border-emerald-200 dark:border-emerald-900/60"
                    data-testid={`${testId}-resolved-athlete-${a.id}`}
                    title={a.squad ? `${a.full_name} · ${a.squad}` : a.full_name}
                  >
                    <AthleteAvatar
                      photoUrl={a.profile_photo}
                      thumbUrl={a.profile_photo_thumbnail}
                      name={a.full_name}
                      size="xs"
                    />
                    {a.full_name}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {showAthleteSelect && !isAll && (
        <div className="mt-1" data-testid={`${testId}-athlete-picker`}>
          <SearchableAthleteSelect
            athletes={selectableAthletes}
            value={selectedAthletes}
            onChange={handleAthletesChange}
            multi={true}
            placeholder="Search & select specific athletes..."
            data-testid={`${testId}-athlete-select`}
          />
        </div>
      )}
    </div>
  );
};

export default TargetAudienceSelector;
